/**
 * Auth State Slice
 *
 * Responsibility: Authentication and user session state
 * This is the ONLY place where domain auth data should live in Redux.
 *
 * Contains:
 * - Current authenticated user info
 * - Access token
 * - Authentication status
 *
 * NOTE: User details and staff lists belong in RTK Query cache!
 * This slice is only for auth context and user identification.
 */

import {
  createSlice,
  createListenerMiddleware,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Staff,AuthState } from "../../types";
import { useAppSelector } from "../index";

// Auth State interface matching the API response


// Storage key for localStorage persistence
export const AUTH_STORAGE_KEY = "miro.sm.AUTH";

// Load initial state from localStorage
const loadInitialState = (): AuthState => {
  try {
    const json = localStorage.getItem(AUTH_STORAGE_KEY);
    if (json) {
      const stored = JSON.parse(json);
      return {
        user: stored.user,
        access_token: stored.access_token,
        isAuthenticated: !!stored.access_token,
        isLoading: false,
      };
    }
  } catch (error) {
    console.error("Failed to load auth state from localStorage:", error);
  }

  return {
    user: null,
    access_token: null,
    isAuthenticated: false,
    isLoading: false,
  };
};

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      { payload }: PayloadAction<{ user: Staff; access_token: string }>,
    ) => {
      state.user = payload.user;
      state.access_token = payload.access_token;
      state.isAuthenticated = true;
    },
    updateUser: (state, { payload }: PayloadAction<Staff>) => {
      state.user = payload || null;
    },
    clearCredentials: (state) => {
      state.user = null;
      state.access_token = null;
      state.isAuthenticated = false;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
  },
});

export const { setCredentials, updateUser, clearCredentials, setLoading } =
  authSlice.actions;

// Auth listener middleware for localStorage persistence and auto-logout
export const authListener = createListenerMiddleware<{ auth: AuthState }>();

// Persist auth state to localStorage
authListener.startListening({
  actionCreator: setCredentials,
  effect: ({ payload }) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
  },
});

// Update localStorage when user profile is updated
authListener.startListening({
  actionCreator: updateUser,
  effect: ({ payload }, api) => {
    const state = api.getState() as { auth: AuthState };
    const authData = {
      user: payload,
      access_token: state.auth.access_token,
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
  },
});

// Clear localStorage on logout
authListener.startListening({
  actionCreator: clearCredentials,
  effect: () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  },
});

// Auto-logout on 401 errors
authListener.startListening({
  predicate: isRejectedWithValue,
  effect(action, api) {
    const state = api.getState() as { auth: AuthState };
    const authToken = state.auth?.access_token;
    const authPayload = action.payload as { status: number };

    if (authPayload?.status === 401 && authToken) {
      console.log("Auto-logout due to 401 error");
      api.dispatch(clearCredentials());
    }
  },
});

// Custom hook to get auth state
export const useAuthState = () => useAppSelector((state) => state.auth);

// Custom hook to get current user
export const useCurrentUser = () => useAppSelector((state) => state.auth.user);

// Custom hook to get auth token
export const useAuthToken = () =>
  useAppSelector((state) => state.auth.access_token);

export default authSlice.reducer;
